import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlContainer, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { IdleStateTrackerService } from '@klickdata/core/application';
import { PromptState } from '@klickdata/core/application/src/model/model-interface';
import { AuthService } from '@klickdata/core/auth';
import { FormHelper } from '@klickdata/core/form';
import { Media } from '@klickdata/core/media/src/media.model';
import { MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import {
    Resource,
    ResourceCategoryService,
    ResourceData,
    ResourceService,
    ResourceTypes,
} from '@klickdata/core/resource';
import { ResourceItem, ResourceItemService } from '@klickdata/core/resource-item';
import { ResourceActionHandlerService } from '@klickdata/core/resource/src/resource-action-handler.service';
import { ResourceComposerDirective } from '@klickdata/core/resource/src/resource-composer-directive';
import { Utils } from '@klickdata/core/util';
import { BehaviorSubject, Observable, of, switchMap, takeUntil } from 'rxjs';
import { VideoLinkControlComponent } from '../video-link-control/video-link-control.component';

@Component({
    selector: 'app-scorm-manager-core',
    templateUrl: './scorm-manager-core.component.html',
    styleUrls: ['./scorm-manager-core.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class ScormManagerCoreComponent extends ResourceComposerDirective implements OnInit {
    @Output() saving: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onSocketResponse: EventEmitter<Resource> = new EventEmitter<Resource>();
    @Input() bottomToolSheet: boolean;
    @ViewChild(VideoLinkControlComponent) videoLinkControl: VideoLinkControlComponent;
    public scormMedia: Media;
    typeId = ResourceTypes.SCORM;
    @Input() resource: Resource | null;
    public resourceItems$: Observable<ResourceItem[]>;
    Utils = Utils;
    public scormParserPromptStateSub: BehaviorSubject<PromptState> = new BehaviorSubject<PromptState>(null);
    public publish: boolean;
    private resourceActionHandlerService = inject(ResourceActionHandlerService);
    constructor(
        protected resourceService: ResourceService,
        protected categoryService: ResourceCategoryService,
        protected auth: AuthService,
        protected parentFormDirective: FormGroupDirective,
        protected messageService: MessageService,
        protected router: Router,
        protected resourceItemService: ResourceItemService,
        protected bottomSheet: MatBottomSheet,
        protected idleStateTracker: IdleStateTrackerService
    ) {
        super(
            resourceService,
            categoryService,
            auth,
            parentFormDirective,
            messageService,
            router,
            bottomSheet,
            idleStateTracker
        );
    }
    ngOnInit(): void {
        super.ngOnInit();
        this.resourceForm.addControl('scorm_uri', new FormControl(''));
        this.resourceForm.addControl('collaboration', new FormGroup({}));
        this.resourceForm.addControl('article_code', new FormControl(this.resource ? this.resource.article_code : ''));
        this.getScormStatus();
    }
    public onTabSelectionChange() {}

    private getScormStatus() {
        this.scormParserPromptStateSub.next(
            this.resource?.prompt_states?.find((state) => state.type === 'scorm_parser')
        );
    }
    onPlay() {
        this.resource.playableResource$
            .pipe(
                switchMap((resPlayableSpec) =>
                    this.resourceActionHandlerService.playResource({ resource: this.resource, resPlayableSpec })
                ),
                takeUntil(this.destroy)
            )

            .subscribe();
    }
    onResourceChanged(created: boolean) {
        this.typeId = this.resource.type_id;
        if (this.resourceForm) {
            this.resourceForm.get('scorm_uri')?.patchValue(this.resource.scorm_uri);
        }
        this.getScormStatus();
        if (this.resource.id) {
            this.resourceItems$ = this.resourceItemService.getResourceItems(this.resource.id);
        }
    }

    protected onRemoteResourceUpdate(resource: Resource): void {
        this.onSocketResponse.emit(resource);
    }

    onScormUploaded(media: Media) {
        this.resourceForm.get('scorm_uri').patchValue(media.path + '/' + media.filename);
        this.resourceForm
            .get('title')
            .patchValue(
                this.resourceForm.value.title === '' ? media.filename : this.resourceForm.value.title ?? media.filename
            );
        super.performResSubmit({ eager: 'scorm' });
    }
    onRemove() {
        this.resourceForm.get('scorm_uri').patchValue(null);
        // this.resourceForm.get('scorm_uri').markAsDirty();
        this.scormMedia = null;
        this.scormParserPromptStateSub.next(null);
    }

    onRetry(prompt: string) {
        this.scormParserPromptStateSub.next({ type: 'scorm_parser', status: 'PROCESSING' });
        this.resourceForm.get('scorm_uri').patchValue(prompt);
        super.performResSubmit({ eager: 'scorm' });
    }

    public onSubmit(publish?: boolean) {
        this.publish = publish;
        this.loading.emit(true);
        this.checkSubmitValid();
    }

    checkSubmitValid() {
        if (this.resourceForm.invalid) {
            FormHelper.markForm(this.resourceForm);
            this.openMessage(
                MessageErrorComponent,
                $localize`${FormHelper.formatInvalidForm(this.resourceForm, this.formats)} is missing`
            );

            this.loading.emit(false);
            return of(false);
        }

        if (!!this.resourceForm.value.publish && !this.resourceForm.value.scorm_uri && !this.resourceItems$) {
            this.openMessage(MessageErrorComponent, $localize`SCORM file is required when you publish the SCORM`);
            this.loading.emit(false);
            return of(false);
        }
        super.performResSubmit({ eager: 'scorm' });
        return of(true);
    }
    protected prepareForm(): Observable<ResourceData> {
        const data = this.resource
            ? this.resource.getData(this.resourceForm.value)
            : new Resource(this.resourceForm.value).getData();

        if (this.scormMedia) {
            data.scorm_uri = this.scormMedia.path + '/' + this.scormMedia.filename;
        }
        if (!this.resource?.type_id) {
            data.type_id = ResourceTypes.SCORM_12;
        }
        // if (this.publish && !data.id) {
        //     data.published = moment().format('YYYY-MM-DD HH:mm:ss');
        // }
        // if (!this.publish) {
        //     data.last_publish = null;
        // }

        return of(data);
    }
    public getResKeysValidaty(): { [key: string]: string | boolean }[] {
        return [
            {
                key: 'title',
                title: $localize`SCORM title`,
                hasValidValue: this.resourceForm.get('title').valid,
                mandatory: true,
            },
            {
                key: 'language_id',
                title: $localize`SCORM language`,
                hasValidValue: this.resourceForm.get('language_id').valid,
                mandatory: true,
            },
            {
                key: 'category_ids',
                title: $localize`SCORM categories`,
                hasValidValue: this.resourceForm.get('category_ids').valid,
                mandatory: true,
            },
            {
                key: 'media_id',
                title: $localize`SCORM image`,
                hasValidValue: this.resourceForm.get('media_id').value,
                mandatory: false,
            },
            {
                key: 'tag_ids',
                title: $localize`SCORM tags`,
                hasValidValue: this.resourceForm.value.tag_ids?.length,
                mandatory: false,
            },
            {
                key: 'bullets',
                title: $localize`SCORM Summary`,
                hasValidValue: this.resourceForm.value.bullets,
                mandatory: false,
                info: $localize`The short summary of this SCORM that will be displayed udner the title (max 256 characters).`,
            },
            {
                key: 'description',
                title: $localize`SCORM description`,
                info: $localize`A description of the SCORM that will be visible befor you take the SCORM.`,
                hasValidValue: this.resourceForm.value.description,
                mandatory: false,
            },
            {
                key: 'instructions',
                title: $localize`Other information`,
                hasValidValue: this.resourceForm.value.instructions,
                mandatory: false,
                info: $localize`Other information that is relevant to this SCORM.`,
            },
        ];
    }
}
